import type { IStatusRule } from "components/shared/StatusChip";
import { formatLocaleDateString } from "helpers/utils";
import type { TFunction } from "i18next";
import type { IDocumentCell } from "store/beneficiaryDocumentsOverviewStore";
import type { IBeneficiaryDocumentType, TDocumentTypeCriteria } from "store/beneficiaryDocumentTypesStore";
import type { IDocument } from "store/documentStore";

export type TDocumentStatus = "missing" | "pending" | "valid" | "expiring_soon" | "expired" | "rejected";

/**
 * Status of one row of the documents tab: the tracked statuses, plus `upcoming` (valid but
 * not in force yet), `history` (a valid document superseded by a more recent one of the
 * same type) and `untracked` (no type).
 */
export type TDocumentRowStatus = TDocumentStatus | "upcoming" | "history" | "untracked";

/** Summary sent with each beneficiary of the list when the tracking is enabled */
export type TDocumentsStatusLevel = "ok" | "warn" | "ko";
export interface IDocumentsStatus {
    level: TDocumentsStatusLevel;
    pendingCount: number;
}

/** Same threshold as the backend overview */
export const EXPIRING_SOON_DAYS = 30;

const pad = (n: number) => String(n).padStart(2, "0");

/** `YYYY-MM-DD` of a Date (local day) or of an ISO / date string. */
export const toDateOnly = (value: Date | string | null | undefined): string | null => {
    if (!value) return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime())
            ? null
            : `${String(value.getFullYear()).padStart(4, "0")}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
    }

    return String(value).slice(0, 10);
};

/** Local Date at midnight from a `YYYY-MM-DD` string (for the date pickers). */
export const fromDateOnly = (value: string | null | undefined): Date | null => {
    if (!value) return null;
    const [y, m, d] = value.slice(0, 10).split("-").map(Number);
    const date = new Date(y, m - 1, d);
    // The constructor maps years 0-99 to 1900-1999: keep the year the picker is being typed with
    date.setFullYear(y);

    return date;
};

/** Earliest plausible year for a document validity date; also the pickers' minDate */
export const MIN_DOCUMENT_YEAR = 1900;

export const addDays = (dateOnly: string, days: number) => {
    const [y, m, d] = dateOnly.split("-").map(Number);

    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

/** UTC day, same as the API. Picker values stay on the local calendar via `toDateOnly`. */
export const todayDateOnly = () => new Date().toISOString().slice(0, 10);

/** A fully typed `YYYY-MM-DD` date: the pickers emit partial years (e.g. 0002) while the user types. */
export const isCompleteDateOnly = (value: string | null | undefined) =>
    !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number(value.slice(0, 4)) >= MIN_DOCUMENT_YEAR;

/** Short numeric date of a date-only value, in UTC so the day never shifts */
export const formatShortDate = (value: string) =>
    formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } });

/** Status of one document row (the beneficiary × type cell status comes from the backend). */
export const getDocumentStatus = (
    document: Pick<IDocument, "status" | "validUntil">,
    today = todayDateOnly(),
): TDocumentStatus => {
    if (document.status === "pending") return "pending";
    if (document.status === "rejected") return "rejected";
    const validUntil = toDateOnly(document.validUntil);
    if (!validUntil) return "valid";
    if (validUntil < today) return "expired";
    if (validUntil < addDays(today, EXPIRING_SOON_DAYS)) return "expiring_soon";

    return "valid";
};

/**
 * The document of a type that currently sets its status: the valid one in force with the
 * latest end date (an open-ended one wins), the most recent on a tie. Same rule as the backend.
 */
export const getCurrentDocumentId = (
    documentTypeId: number,
    documents: Pick<IDocument, "id" | "documentTypeId" | "status" | "validFrom" | "validUntil">[],
    today = todayDateOnly(),
): number | null => {
    const candidates = documents.filter((document) => {
        if (document.documentTypeId !== documentTypeId || document.status !== "valid") return false;
        const from = toDateOnly(document.validFrom);

        return !from || from <= today;
    });
    const [best] = [...candidates].sort((a, b) => {
        const ua = toDateOnly(a.validUntil);
        const ub = toDateOnly(b.validUntil);
        if (ua === ub) return b.id - a.id;
        if (ua === null) return -1;
        if (ub === null) return 1;

        return ub.localeCompare(ua);
    });

    return best?.id ?? null;
};

/** Status of one row of the documents table, among all the documents of the beneficiary. */
export const getDocumentRowStatus = (
    document: Pick<IDocument, "id" | "documentTypeId" | "status" | "validFrom" | "validUntil">,
    documents: Pick<IDocument, "id" | "documentTypeId" | "status" | "validFrom" | "validUntil">[],
    today = todayDateOnly(),
): TDocumentRowStatus => {
    if (document.status === "pending" || document.status === "rejected") return document.status;
    if (!document.documentTypeId) return "untracked";
    const from = toDateOnly(document.validFrom);
    if (from && from > today) return "upcoming";
    if (getCurrentDocumentId(document.documentTypeId, documents, today) !== document.id) return "history";

    return getDocumentStatus(document, today);
};

/** A type is expected from a beneficiary when every non-null criterion matches their tax profile. */
export interface ITaxProfile {
    precount?: number | string | null;
    vat?: number | string | null;
    entity?: string | null;
    country?: string | null;
    socialProfileType?: string | null;
}

export const isTypeApplicable = (criteria: TDocumentTypeCriteria, beneficiary: ITaxProfile): boolean => {
    if (criteria.precount !== null && Number(beneficiary.precount ?? 0) !== criteria.precount) return false;
    if (criteria.vatProfiles?.length && !criteria.vatProfiles.includes(Number(beneficiary.vat ?? 0))) return false;
    if (criteria.entity && beneficiary.entity !== criteria.entity) return false;
    if (criteria.countries?.length) {
        const country = (beneficiary.country ?? "").trim().toLowerCase();
        const listed = criteria.countries.some((c) => c.trim().toLowerCase() === country);
        // "Outside these countries" needs a known residence: an empty country is not abroad
        if (criteria.excludeCountries ? !country || listed : !listed) return false;
    }
    if (criteria.socialProfileTypes?.length) {
        if (!criteria.socialProfileTypes.includes(String(beneficiary.socialProfileType ?? ""))) return false;
    }

    return true;
};

/** Active types expected from the beneficiary, in the given order (none without a beneficiary). */
export const expectedTypes = <T extends Pick<IBeneficiaryDocumentType, "criteria" | "isActive">>(
    types: T[],
    beneficiary?: ITaxProfile,
): T[] => (beneficiary ? types.filter((type) => type.isActive && isTypeApplicable(type.criteria, beneficiary)) : []);

export const documentStatusRules = (t: TFunction): Record<TDocumentRowStatus, IStatusRule> => ({
    valid: { color: "green", text: t("BeneficiaryDocuments.Status.valid") },
    expiring_soon: { color: "yellow", text: t("BeneficiaryDocuments.Status.expiring_soon") },
    expired: { color: "red", text: t("BeneficiaryDocuments.Status.expired") },
    missing: { color: "grey", text: t("BeneficiaryDocuments.Status.missing") },
    pending: { color: "blue", text: t("BeneficiaryDocuments.Status.pending") },
    rejected: { color: "grey", text: t("BeneficiaryDocuments.Status.rejected") },
    upcoming: { color: "blue", text: t("BeneficiaryDocuments.Status.upcoming") },
    history: { color: "grey", text: t("BeneficiaryDocuments.Status.history") },
    untracked: { color: "grey", text: t("BeneficiaryDocuments.Status.untracked") },
});

/** Label of the documents dot of the beneficiaries list. */
export const documentsStatusLabel = (t: TFunction, status: IDocumentsStatus) => {
    const label = t(`BeneficiaryDocuments.ListStatus.${status.level}`);

    return status.pendingCount
        ? `${label} · ${t("BeneficiaryDocuments.ListStatus.pending", { pending: status.pendingCount })}`
        : label;
};

/** A type expected from the beneficiary but not satisfied: what `{{documents}}` lists in the reminder email. */
export type TOutdatedStatus = "missing" | "expired" | "expiring_soon";
export interface IOutdatedDocument {
    typeId: number;
    typeName: string;
    status: TOutdatedStatus;
    validUntil: string | null;
}

/** Documents not up to date of a beneficiary, one per expected type (same rule as the backend). */
export const listOutdatedDocuments = (
    types: Pick<IBeneficiaryDocumentType, "id" | "name" | "criteria">[],
    beneficiary: ITaxProfile,
    documents: Pick<IDocument, "id" | "documentTypeId" | "status" | "validFrom" | "validUntil">[],
    today = todayDateOnly(),
): IOutdatedDocument[] =>
    types
        .filter((type) => isTypeApplicable(type.criteria, beneficiary))
        .flatMap((type): IOutdatedDocument[] => {
            // Already sent and waiting for the publisher: the reminder must not ask for it again
            if (documents.some((document) => document.documentTypeId === type.id && document.status === "pending")) {
                return [];
            }
            const currentId = getCurrentDocumentId(type.id, documents, today);
            const current = documents.find((document) => document.id === currentId);
            if (!current) return [{ typeId: type.id, typeName: type.name, status: "missing", validUntil: null }];
            const status = getDocumentStatus(current, today);
            if (status !== "expired" && status !== "expiring_soon") return [];

            return [{ typeId: type.id, typeName: type.name, status, validUntil: toDateOnly(current.validUntil) }];
        });

/** Same list from an overview entry, whose cells the backend already computed. */
export const outdatedDocumentsOfEntry = (
    documents: Record<number, Pick<IDocumentCell, "status" | "validUntil" | "hasPending">>,
    types: Pick<IBeneficiaryDocumentType, "id" | "name">[],
): IOutdatedDocument[] =>
    Object.entries(documents).flatMap(([typeId, cell]) =>
        cell.status === "valid" || cell.hasPending
            ? []
            : [
                  {
                      typeId: Number(typeId),
                      typeName: types.find((type) => type.id === Number(typeId))?.name ?? "",
                      status: cell.status,
                      validUntil: cell.validUntil,
                  },
              ],
    );
